import { TAccessLevel, ISchemaField } from '@/types';
import { IBaseResponse, fetchWrapper } from './apiBase';
import { BASE_API_URL } from '@/config/environment';

export interface IRepositorySchemaResponse extends IBaseResponse {
  schema?: ISchemaSummary;
}
interface IRepositorySchemasResponse extends IBaseResponse {
  schemas?: ISchemaSummary[];
  access_level: TAccessLevel;
}

interface ISchemaFile {
  name: string;
  path: string;
}

export interface ISchemaSummary {
  path?: string;
  files?: ISchemaFile[];
  name?: string | null;
  hash: string;
  fields: ISchemaField[];
  metadata?: any;
}

interface ISchemaInfo {
  schemas: ISchemaSummary[];
  totalFileCount: number;
  totalSchemaCount: number;
}

const getSchemaPathFileName = (path: string) => {
  return path.split('/')[path.split('/').length - 1];
};

const getDistinctSchemaHashes = (schemas: ISchemaSummary[]) => {
  const schemaHashes = schemas.map((schema) => schema.hash);
  return Array.from([...new Set(schemaHashes)]);
};

const getSchemaFile = (schema: ISchemaSummary): ISchemaFile => {
  if (!schema.path) return { name: '', path: '' };
  return { name: getSchemaPathFileName(schema.path), path: schema.path };
};

const getSchemaRollup = (schemas: ISchemaSummary[], schemaHash: string): ISchemaSummary => {
  const matchingSchemas = schemas.filter((schema) => schema.hash === schemaHash);
  const files: ISchemaFile[] = matchingSchemas.map((schema) => getSchemaFile(schema));
  const thisSchema = { ...matchingSchemas[0] };
  return { ...thisSchema, hash: schemaHash, files };
};

export const getSchemasInfo = (schemas: ISchemaSummary[]): ISchemaInfo => {
  const totalFileCount = schemas.length;
  const schemaHashes = getDistinctSchemaHashes(schemas);
  const totalSchemaCount = schemaHashes.length;
  const distinctSchemas = schemaHashes?.map((schemaHash) => getSchemaRollup(schemas, schemaHash));
  distinctSchemas.sort((a, b) => ((a.files?.length || 0) > (b.files?.length || 0) ? -1 : 1));
  return { schemas: distinctSchemas, totalFileCount, totalSchemaCount };
};

export const fetchSchemas = async (
  namespace: string,
  repositoryName: string,
  commitRef?: string,
  token?: string,
): Promise<IRepositorySchemasResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/schemas/${commitRef || 'main'}`;

  return await fetchWrapper({
    resourceName: 'schemas',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, commitRef: commitRef || 'main' },
  });
};

// https://staging.hub.oxen.ai/api/repos/ox/Kitchen-Sink/schemas/hash/4f042b6fed34cf37668483591d11d38a

type TFetchSchemaRequestParams = {
  namespace: string;
  repositoryName: string;
  schema_hash?: string;
  token?: string;
};

export const fetchSchema = async ({
  namespace,
  repositoryName,
  schema_hash,
  token,
}: TFetchSchemaRequestParams): Promise<IRepositorySchemaResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/schemas/hash/${schema_hash}`;

  return await fetchWrapper({
    resourceName: 'schema',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, schema_hash },
  });
};
